/**
 * Patient Name Utilities
 * 
 * Helper functions for formatting patient names consistently across the application.
 * Database stores: patientFirstName and patientLastName
 * Display format: "Lastname, Firstname"
 * Filename format: "Lastname Firstname" (no comma, for file system compatibility)
 */

/**
 * Format patient name for display (with comma)
 * @param {string} firstName - Patient's first name
 * @param {string} lastName - Patient's last name
 * @returns {string} Formatted name as "Lastname, Firstname"
 */
export const formatPatientNameForDisplay = (firstName, lastName) => {
    if (!lastName || lastName === 'Patient' || lastName === 'Unknown') {
        return lastName || 'Patient';
    }
    if (!firstName) {
        return lastName;
    }
    return `${lastName}, ${firstName}`;
};

/**
 * Format patient name for filenames (without comma)
 * @param {string} firstName - Patient's first name
 * @param {string} lastName - Patient's last name
 * @returns {string} Formatted name as "Lastname Firstname" (space separated, no comma)
 */
export const formatPatientNameForFilename = (firstName, lastName) => {
    if (!lastName || lastName === 'Patient' || lastName === 'Unknown') {
        return lastName || 'patient';
    }
    if (!firstName) {
        return lastName;
    }
    return `${lastName} ${firstName}`.replace(/\s+/g, ' ').trim();
};

/**
 * Get patient name initial for avatar
 * @param {string} firstName - Patient's first name
 * @param {string} lastName - Patient's last name
 * @returns {string} First character of the last name (uppercase)
 */
export const getPatientInitial = (firstName, lastName) => {
    const name = lastName || firstName || 'P';
    return name.charAt(0).toUpperCase();
};

/**
 * Split a combined patient name into first and last names
 * Legacy function for backward compatibility when dealing with old "Firstname Lastname" format
 * @param {string} fullName - Combined name as "Firstname Lastname"
 * @returns {{firstName: string, lastName: string}} Object with firstName and lastName
 */
export const splitPatientName = (fullName) => {
    if (!fullName || fullName.trim() === '') {
        return { firstName: '', lastName: '' };
    }

    const parts = fullName.trim().split(' ');

    if (parts.length === 1) {
        return { firstName: '', lastName: parts[0] };
    }

    // Last word is lastname, rest is firstname
    const lastName = parts[parts.length - 1];
    const firstName = parts.slice(0, -1).join(' ');

    return { firstName, lastName };
};
